import React from 'react';

interface FinalCTASectionProps {
  headline?: string;
  description?: string;
  ctaButtonText?: string;
  ctaButtonHref?: string;
  onCTAClick?: () => void;
  backgroundVariant?: 'gradient' | 'solid' | 'image';
  className?: string;
}

const FinalCTASection: React.FC<FinalCTASectionProps> = ({
  headline = "Ready to Start Your Transformation?",
  description = "Connect with our AWS experts to discover how our solutions can drive your business forward",
  ctaButtonText = "Schedule a Discovery Workshop",
  ctaButtonHref = "/contact",
  onCTAClick,
  backgroundVariant = 'gradient',
  className = ''
}) => {
  const handleCTAClick = () => {
    if (onCTAClick) {
      onCTAClick();
    }
  };

  const getBackgroundClasses = () => {
    switch (backgroundVariant) {
      case 'gradient':
        return 'bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800';
      case 'solid':
        return 'bg-blue-600';
      case 'image':
        return 'bg-blue-600 bg-cover bg-center relative';
      default:
        return 'bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800';
    }
  };

  return (
    <section className={`py-20 ${getBackgroundClasses()} ${className}`}>
      {backgroundVariant === 'image' && (
        <div className="absolute inset-0 bg-blue-900 bg-opacity-80"></div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {headline}
          </h2>
          
          <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {ctaButtonHref ? (
              <a
                href={ctaButtonHref}
                onClick={handleCTAClick}
                className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-lg"
              >
                {ctaButtonText}
                <svg
                  className="ml-2 w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </a>
            ) : (
              <button
                onClick={handleCTAClick}
                className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-lg"
              >
                {ctaButtonText}
                <svg
                  className="ml-2 w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>
            )}
            
            <a
              href="/solutions"
              className="inline-flex items-center text-white hover:text-blue-200 font-medium py-4 px-6 rounded-lg transition-colors duration-200 text-lg border border-white border-opacity-30 hover:border-opacity-50"
            >
              Explore Solutions
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </a>
          </div>
          
          {/* Additional trust indicators */}
          <div className="mt-12 pt-8 border-t border-white border-opacity-20">
            <p className="text-blue-200 text-sm mb-4">
              Join 500+ companies that have accelerated their transformation with HCLTech
            </p>
            <div className="flex flex-wrap justify-center items-center gap-6 text-blue-200 text-sm">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Free Discovery Workshop
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                No Commitment Required
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Expert AWS Guidance
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;